import { Line, LineChart } from "recharts";
import { ChartContainer, type ChartConfig } from "~/components/ui/chart";
import { cn } from "~/utils";

export interface SparklineSeries {
  key: string;
  color: string;
  // `null` marks a gap with no data; the line bridges across it (connectNulls)
  // rather than dropping to zero. Callers that never have gaps pass plain numbers.
  points: (number | null)[];
}

export interface SparklineProps {
  series: SparklineSeries[];
  className?: string;
  testId?: string;
}

export const Sparkline = ({ series, className, testId }: SparklineProps) => {
  const pointCount = Math.max(0, ...series.map((entry) => entry.points.length));
  const data = Array.from({ length: pointCount }, (_, index) => {
    const row: Record<string, number | string | null> = { index };
    series.forEach((entry) => {
      row[entry.key] = entry.points[index] ?? null;
    });
    return row;
  });

  const config = Object.fromEntries(
    series.map((entry) => [
      entry.key,
      { label: entry.key, color: entry.color },
    ]),
  ) satisfies ChartConfig;

  if (pointCount === 0) {
    return (
      <div
        data-testid={testId}
        className={cn("h-12 w-full rounded-md bg-muted/30", className)}
      />
    );
  }

  // Renders a single solid dot at the last point of each line as an end cap.
  const endCapDot = (props: {
    cx?: number;
    cy?: number;
    index?: number;
    stroke?: string;
  }) => {
    const { cx, cy, index, stroke } = props;
    if (index !== pointCount - 1 || cx == null || cy == null || !stroke) {
      return null;
    }

    return (
      <ellipse cx={cx} cy={cy} rx={3} ry={3} fill={stroke} fillOpacity={0.9} />
    );
  };

  return (
    <ChartContainer
      config={config}
      data-testid={testId}
      className={cn(
        "pointer-events-none aspect-auto h-12 w-full select-none",
        "[&_.recharts-wrapper]:outline-none [&_.recharts-surface]:outline-none",
        className,
      )}
    >
      <LineChart data={data} margin={{ top: 4, right: 10, bottom: 4, left: 4 }}>
        {series.map((entry) => (
          <Line
            key={entry.key}
            type="monotone"
            dataKey={entry.key}
            stroke={`var(--color-${entry.key})`}
            strokeWidth={1.5}
            strokeLinecap="round"
            dot={endCapDot}
            activeDot={false}
            connectNulls
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ChartContainer>
  );
};
